import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppComponent } from './app.component';
import { ApiModule, BASE_PATH } from '../swagger';
import { environment } from '../environments/environment';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './routing.module';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

const ZhylarPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#f3eefb',
            100: '#e0d3f5',
            200: '#c2a3f3',
            300: '#a880e9',
            400: '#9c6de7',
            500: '#8b59da',
            600: '#7440c9',
            700: '#5e33a4',
            800: '#4a2880',
            900: '#351d5c',
            950: '#241340',
        },
        formField: {
            paddingX: '0.5rem',
            paddingY: '0.4rem',
            borderRadius: '4px',
            borderColor: '{surface.300}',
            hoverBorderColor: '{surface.900}',
            focusBorderColor: '{surface.900}',
            invalidBorderColor: '#e4677e',
            disabledBackground: '#f7f9fb',
            disabledColor: '#6c757d',
            focusRing: {
                width: '0',
                color: 'transparent',
            },
            shadow: 'none',
        },
        colorScheme: {
            light: {
                surface: {
                    200: '#dee2e6',
                    300: '#ced4da',
                },
                formField: {
                    borderColor: '#ced4da',
                    hoverBorderColor: '#1c1c1c',
                    focusBorderColor: '#1c1c1c',
                    disabledBackground: '#f7f9fb',
                },
            },
        },
    },
    components: {
        inputgroup: {
            addon: {
                background: '#e9ecef',
                borderColor: '#ced4da',
                padding: '0.4rem 8px',
            },
        },
    },
});
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ZhylarToastMessageService } from './zhylar-toast-message.service';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthInterceptor } from './auth-interceptor';
import { FormatPersonNamesInterceptor } from './format-person-names.interceptor';
import { RECAPTCHA_SETTINGS, RECAPTCHA_V3_SITE_KEY, RecaptchaSettings } from 'ng-recaptcha';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { SocketIoModule } from 'ngx-socket-io';
import { OrgSocket } from '../socket/org-socket';
import { OrgSubscriptionSocket } from '../socket/org-subscription-socket';
import { PartnerSocket } from '../socket/partner-socket';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [CommonModule,
        BrowserModule,
        AppRoutingModule,
        ApiModule,
        BrowserAnimationsModule,
        ButtonModule,
        ConfirmDialogModule,
        ToastModule,
        CalendarModule.forRoot({
            provide: DateAdapter,
            useFactory: adapterFactory,
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
            defaultLanguage: 'en',
        }),
        SocketIoModule], providers: [
        { provide: BASE_PATH, useValue: environment.BASE_PATH },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FormatPersonNamesInterceptor,
            multi: true,
        },
        {
            provide: RECAPTCHA_SETTINGS,
            useValue: {
                siteKey: environment.RECAPTCHA_SITE_KEY,
            } as RecaptchaSettings,
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.RECAPTCHA_SITE_KEY,
        },
        { provide: MessageService, useClass: ZhylarToastMessageService },
        ConfirmationService,
        OrgSocket,
        OrgSubscriptionSocket,
        PartnerSocket,
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
            theme: {
                preset: ZhylarPreset,
                options: {
                    darkModeSelector: false,
                },
            },
            overlayAppendTo: 'body',
        }),
    ] })
export class AppModule {}
